import {
  type Game,
  type GamePlatform,
  type ThemeSensitivity,
  GameSchema,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

type RawGameRow = {
  id: unknown;
  studio_id: unknown;
  name: unknown;
  slug: unknown;
  short_id: unknown;
  settings: unknown;
  target_platforms: unknown;
  theme_sensitivity: unknown;
  created_at: unknown;
  updated_at: unknown;
};

function mapGameRow(raw: RawGameRow): Record<string, unknown> {
  return {
    id: raw.id,
    studioId: raw.studio_id,
    name: raw.name,
    slug: raw.slug,
    shortId: raw.short_id,
    settings: raw.settings,
    targetPlatforms: raw.target_platforms ?? [],
    themeSensitivity: raw.theme_sensitivity,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export type GamePatch = {
  name?: string;
  targetPlatforms?: GamePlatform[];
  themeSensitivity?: ThemeSensitivity;
};

export type CreateGameInput = {
  studioId: string;
  name: string;
  targetPlatforms: GamePlatform[];
};

export async function createGame(input: CreateGameInput): Promise<Game> {
  const { data, error } = await supabase.rpc('create_game', {
    p_studio_id: input.studioId,
    p_name: input.name,
    p_target_platforms: input.targetPlatforms,
  });

  if (error) {
    console.error('[game-data] Failed to create game:', error);
    throw new Error(error.message);
  }

  // The RPC returns the row directly. Supabase sometimes wraps single-row
  // returns in an array; handle both.
  const row = Array.isArray(data) ? data[0] : data;
  if (row === undefined || row === null) {
    throw new Error('create_game returned an empty response.');
  }

  const parsed = GameSchema.safeParse(mapGameRow(row as RawGameRow));
  if (!parsed.success) {
    console.error('[game-data] Could not parse created game row.', parsed.error.issues, row);
    throw new Error('Could not parse the created game row.');
  }
  return parsed.data;
}

export async function updateGame(gameId: string, patch: GamePatch): Promise<Game | null> {
  const update: Record<string, unknown> = {};
  if (patch.name !== undefined) update.name = patch.name;
  if (patch.targetPlatforms !== undefined) update.target_platforms = patch.targetPlatforms;
  if (patch.themeSensitivity !== undefined) update.theme_sensitivity = patch.themeSensitivity;

  const { data, error } = await supabase
    .from('games')
    .update(update)
    .eq('id', gameId)
    .select('*')
    .single();

  if (error) {
    console.warn('[game-data] Failed to update game:', error.message);
    return null;
  }

  const parsed = GameSchema.safeParse(mapGameRow(data as RawGameRow));
  if (!parsed.success) {
    console.warn('[game-data] Could not parse updated game row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}
